import { createSlice } from '@reduxjs/toolkit';
import {
  createPaymentForOrderThunk,
  fetchOrderPaymentsThunk,
  updatePaymentStatusThunk
} from './paymentsThunks';
import { type IOrderPayment } from '@/types/IOrder';
import { type IErrorData } from '@/utils/getErrorData';

interface PaymentsState {
  /** Платежи текущего заказа: с 06.08.2026 приходят отдельной ручкой. */
  items: IOrderPayment[];
  /** UUID заказа, к которому относится `items`. */
  orderUuid: string | null;
  isLoading: boolean;
  creatingPayment: boolean;
  updatingPaymentById: Record<number, boolean>;
  error: IErrorData | null;
}

const initialState: PaymentsState = {
  items: [],
  orderUuid: null,
  isLoading: false,
  creatingPayment: false,
  updatingPaymentById: {},
  error: null
};

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    resetPaymentsState(state) {
      state.items = [];
      state.orderUuid = null;
      state.isLoading = false;
      state.creatingPayment = false;
      state.updatingPaymentById = {};
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderPaymentsThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        // Заказ сменился — старые платежи не показываем даже на время загрузки.
        if (state.orderUuid !== action.meta.arg.orderUuid) {
          state.items = [];
          state.orderUuid = action.meta.arg.orderUuid;
        }
      })
      .addCase(fetchOrderPaymentsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.orderUuid = action.meta.arg.orderUuid;
      })
      .addCase(fetchOrderPaymentsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? null;
      });

    builder
      .addCase(createPaymentForOrderThunk.pending, (state) => {
        state.creatingPayment = true;
        state.error = null;
      })
      .addCase(createPaymentForOrderThunk.fulfilled, (state) => {
        state.creatingPayment = false;
      })
      .addCase(createPaymentForOrderThunk.rejected, (state, action) => {
        state.creatingPayment = false;
        state.error = action.payload ?? null;
      });

    builder
      .addCase(updatePaymentStatusThunk.pending, (state, action) => {
        const paymentId = action.meta.arg.paymentId;
        state.updatingPaymentById[paymentId] = true;
        state.error = null;
      })
      .addCase(updatePaymentStatusThunk.fulfilled, (state, action) => {
        const paymentId = action.meta.arg.paymentId;
        state.updatingPaymentById[paymentId] = false;
      })
      .addCase(updatePaymentStatusThunk.rejected, (state, action) => {
        const paymentId = action.meta.arg.paymentId;
        state.updatingPaymentById[paymentId] = false;
        state.error = action.payload ?? null;
      });
  }
});

export const { resetPaymentsState } = paymentsSlice.actions;
export default paymentsSlice.reducer;
